import { useEffect, useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { Flex, Spinner } from '@radix-ui/themes';
import cognitoProvider from '../../../cognito';
import useAuthStore from '../../../store/authStore';
import { AuthGroup, parseGroups } from '../../../auth/groups';
import { parsePermissions } from '../../../auth/permissions';

type RoleGuardProps = {
  requiredGroups: AuthGroup[];
};

function RoleGuard({ requiredGroups }: RoleGuardProps) {
  const setAuthGroups = useAuthStore((s) => s.setAuthGroups);
  const setAuthPermissions = useAuthStore((s) => s.setAuthPermissions);
  const setPartnerId = useAuthStore((s) => s.setPartnerId);

  const [checking, setChecking] = useState(true);
  const [authorized, setAuthorized] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      try {
        let session = cognitoProvider.getCurrentSession();

        if (!session?.isValid()) {
          session = await cognitoProvider.refreshCurrentSession();
        }
        if (cancelled) {
          return;
        }
        const payload = (session?.getIdToken().payload ?? {}) as Record<string, unknown>;
        const groups = parseGroups(payload);
        setAuthGroups(groups);
        const permissions = parsePermissions(payload);
        setAuthPermissions(permissions);
        setAuthorized(requiredGroups.some((g) => groups.includes(g)));

        // Extract partnerId from the Cognito ID token custom attribute
        const rawPartnerId = payload['custom:partnerId'];
        if (typeof rawPartnerId === 'string' || typeof rawPartnerId === 'number') {
          const parsed = parseInt(String(rawPartnerId), 10);
          if (!isNaN(parsed)) {
            setPartnerId(parsed);
          }
        }
      } catch {
        if (!cancelled) {
          setAuthorized(false);
        }
      } finally {
        if (!cancelled) {
          setChecking(false);
        }
      }
    };

    check();

    return () => {
      cancelled = true;
    };
  }, [setAuthGroups, setAuthPermissions, setPartnerId, requiredGroups]);

  if (checking) {
    return (
      <Flex align="center" justify="center" style={{ height: '100vh' }}>
        <Spinner size="3" />
      </Flex>
    );
  }

  if (!authorized) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <Outlet />;
}

export default RoleGuard;
